import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';

const Header: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setMobileOpen(false);
  };

  const linkStyle = {
    color: '#1a1a1a',
    fontSize: '0.875rem',
    fontWeight: 600,
    textDecoration: 'none',
    cursor: 'pointer',
    fontFamily: '"Arial", sans-serif',
    '&:hover': { color: '#dc143c' },
  };

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: 'white',
          boxShadow: 'none',
          borderBottom: 'none',
        }}
      >
        <Toolbar
          sx={{
            justifyContent: 'space-between',
            py: 2,
            px: { xs: 2, md: 6 },
            maxWidth: '1400px',
            width: '100%',
            mx: 'auto',
          }}
        >
          {/* Social Icons - Left */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              href="https://www.linkedin.com/in/kevynfrancoexpert/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
              sx={{ color: '#1a1a1a', p: 0.5 }}
            >
              <LinkedInIcon sx={{ fontSize: 20 }} />
            </IconButton>
            <IconButton
              href="https://x.com/kfrancoexpert"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="X (Twitter)"
              sx={{ color: '#1a1a1a', p: 0.5 }}
            >
              <TwitterIcon sx={{ fontSize: 20 }} />
            </IconButton>
            <IconButton
              href="https://instagram.com"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              sx={{ color: '#1a1a1a', p: 0.5 }}
            >
              <InstagramIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>

          {/* Navigation Links - Center */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              gap: 4,
              alignItems: 'center',
            }}
          >
            <Link to="/speaking" style={{ textDecoration: 'none' }}>
              <Box sx={linkStyle}>SPEAKING ENGAGEMENTS</Box>
            </Link>

            <a
              href="#writing"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection('writing');
              }}
              style={{ textDecoration: 'none' }}
            >
              <Box sx={linkStyle}>WRITING</Box>
            </a>

            {/* Logo - Center */}
            <Link to="/" style={{ textDecoration: 'none' }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
              >
                <Box
                  sx={{
                    fontFamily: '"Impact", sans-serif',
                    fontSize: '1.75rem',
                    fontWeight: 900,
                    color: '#1a1a1a',
                    textAlign: 'center',
                    lineHeight: 0.9,
                  }}
                >
                  <Box component="span" sx={{ display: 'block' }}>
                    KEVYN
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      display: 'block',
                      borderTop: '4px solid #dc143c',
                      pt: 0.3,
                    }}
                  >
                    FRANCO
                  </Box>
                </Box>
              </Box>
            </Link>

            <Link to="/consulting" style={{ textDecoration: 'none' }}>
              <Box sx={linkStyle}>CONSULTING</Box>
            </Link>

            <Link to="/philanthropy" style={{ textDecoration: 'none' }}>
              <Box sx={linkStyle}>PHILANTHROPY</Box>
            </Link>
          </Box>

          {/* Menu Icon - Right (Desktop) */}
          <IconButton
            onClick={handleDrawerToggle}
            aria-label="Open menu"
            sx={{ 
              color: '#1a1a1a', 
              p: 1,
              display: { xs: 'none', md: 'block' }
            }}
          >
            <MenuIcon sx={{ fontSize: 28 }} />
          </IconButton>

          {/* Mobile: Logo Center */}
          <Link to="/" style={{ textDecoration: 'none', position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
            <Box
              sx={{
                display: { xs: 'flex', md: 'none' },
                flexDirection: 'column',
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              <Box
                sx={{
                  fontFamily: '"Impact", sans-serif',
                  fontSize: '1.5rem',
                  fontWeight: 900,
                  color: '#1a1a1a',
                  textAlign: 'center',
                  lineHeight: 0.9,
                }}
              >
                <Box component="span" sx={{ display: 'block' }}>
                  KEVYN
                </Box>
                <Box
                  component="span"
                  sx={{
                    display: 'block',
                    borderTop: '3px solid #dc143c',
                    pt: 0.3,
                  }}
                >
                  FRANCO
                </Box>
              </Box>
            </Box>
          </Link>

          {/* Menu Icon - Right (Mobile Only) */}
          <IconButton
            onClick={handleDrawerToggle}
            aria-label="Open menu"
            sx={{ 
              color: '#1a1a1a', 
              p: 1, 
              display: { xs: 'block', md: 'none' } 
            }}
          >
            <MenuIcon sx={{ fontSize: 28 }} />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Drawer Menu */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: 400 },
            backgroundColor: 'white',
            color: '#1a1a1a',
          },
        }}
      >
        <Box sx={{ p: 4 }}>
          {/* Close Button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 4 }}>
            <IconButton onClick={handleDrawerToggle} aria-label="Close menu" sx={{ color: '#1a1a1a' }}>
              <CloseIcon sx={{ fontSize: 32 }} />
            </IconButton>
          </Box>

          {/* Let's Connect Header */}
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2rem', md: '2.5rem' },
              fontWeight: 900,
              fontFamily: '"Impact", sans-serif',
              mb: 3,
              textTransform: 'uppercase',
            }}
          >
            LET'S CONNECT!
          </Typography>

          <Typography
            sx={{
              fontSize: '1.25rem',
              fontWeight: 700,
              mb: 4,
              pb: 3,
              borderBottom: '3px solid #dc143c',
              display: 'inline-block',
            }}
          >
            Contact Me
          </Typography>

          {/* Menu Items */}
          <List sx={{ mt: 4 }}>
            <ListItem disablePadding sx={{ mb: 2 }}>
              <ListItemButton component={Link} to="/speaking" onClick={() => setMobileOpen(false)} sx={{ p: 0 }}>
                <ListItemText
                  primary="SPEAKING ENGAGEMENTS"
                  primaryTypographyProps={{
                    sx: {
                      fontFamily: '"Arial", sans-serif',
                      fontSize: '1.125rem',
                      fontWeight: 400,
                      color: '#1a1a1a',
                    },
                  }}
                />
              </ListItemButton>
            </ListItem>

            <ListItem disablePadding sx={{ mb: 2 }}>
              <ListItemButton component={Link} to="/articles" onClick={() => setMobileOpen(false)} sx={{ p: 0 }}>
                <ListItemText
                  primary="WRITING"
                  primaryTypographyProps={{
                    sx: {
                      fontFamily: '"Arial", sans-serif',
                      fontSize: '1.125rem',
                      fontWeight: 400,
                      color: '#1a1a1a',
                    },
                  }}
                />
              </ListItemButton>
            </ListItem>

            <ListItem disablePadding sx={{ mb: 2 }}>
              <ListItemButton component={Link} to="/consulting" onClick={() => setMobileOpen(false)} sx={{ p: 0 }}>
                <ListItemText
                  primary="CONSULTING"
                  primaryTypographyProps={{
                    sx: {
                      fontFamily: '"Arial", sans-serif',
                      fontSize: '1.125rem',
                      fontWeight: 400,
                      color: '#1a1a1a',
                    },
                  }}
                />
              </ListItemButton>
            </ListItem>

            <ListItem disablePadding sx={{ mb: 2 }}>
              <ListItemButton component={Link} to="/philanthropy" onClick={() => setMobileOpen(false)} sx={{ p: 0 }}>
                <ListItemText
                  primary="PHILANTHROPY"
                  primaryTypographyProps={{
                    sx: {
                      fontFamily: '"Arial", sans-serif',
                      fontSize: '1.125rem',
                      fontWeight: 400,
                      color: '#1a1a1a',
                    },
                  }}
                />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </Drawer>

      {/* Spacer for fixed AppBar */}
      <Toolbar sx={{ mb: 2 }} />
    </>
  );
};

export default Header;
